import type { Api } from '@pflegenavi/shared-frontend/platform';
import { modify } from '@pflegenavi/shared-frontend/platform';
import {
  get,
  getPhoenixApiTenantUrl,
} from '@pflegenavi/shared-frontend/platform';
import type { Tenant } from '@pflegenavi/frontend/tenant';
import type { AuthenticationContext } from '@pflegenavi/frontend/authentication';
import type {
  PaymentInitiatedBy,
  PaymentInitiationDto,
  PaymentProcessor,
  PaymentSchedule,
  PendingChargeStatus,
  SourceOfMandate,
} from '@pflegenavi/shared/api';

export enum MangopayPaymentStatus {
  Pending = 'pending',
  Paid = 'paid',
}

export interface MangopayReceiptBatchEntry {
  receipt_batch_entry_id: string;
  status: MangopayPaymentStatus;
  resident_id: string;
  amount: number;
  additional_fee_amount: number;
  included_fee_amount: number;
  has_pending_transfer?: boolean;
  transfer_datetime?: Date;
}

export interface MangopayReceiptBatchPayment {
  receipt_batch_entries: MangopayReceiptBatchEntry[];
}

export interface GetReceiptBatchPaymentPhoenixResult {
  data: {
    receipt_batch_id: string;
    receipt_batch_payment: MangopayReceiptBatchPayment;
  };
}
export interface Preconditions {
  error: boolean;
  pending_charge_exists: boolean;
  idle: boolean;
  disabled_for_nursing_home: boolean;
  disabled: boolean;
  resident_not_active: boolean;
  stripe_account_deactivated: boolean;
  primary_family_member_missing: boolean;
  direct_debit_missing: boolean;
  amount_too_low: boolean;
}

export interface PendingCharge {
  id: string;
  pending_charge_status: PendingChargeStatus;
  charge_date: Date;
  amount: number;
  currency: 'eur';
  payment_intent_id: string;
  notes: string;
  error: string;
  additional_data: any;
  iban_last_4: string;
  payment_initiation: PaymentInitiatedBy;
  payment_schedule: PaymentSchedule;
  mangopay_payin_id: string;
  payment_processor: PaymentProcessor;
  source_of_mandate: SourceOfMandate;
  family_member_first_name?: string;
  family_member_last_name?: string;
  employee_first_name?: string;
  employee_last_name?: string;
  inserted_at: Date;
}

export interface PaymentInfo {
  resident_id: string;
  preconditions: Preconditions;
  additional_data: {
    nursing_home_payments_disabled_until: Date | undefined;
    nursing_home_payments_disabled_reason: string | undefined;
  };
  resident: {
    wallet_mode: 'resident' | 'family_member';
  };
  resident_family_member: {
    mangopay_kyc_verified: boolean;
  };
  pending_charges: PendingCharge[];
  payment_settings: {
    payment_initiation: PaymentInitiatedBy;
    payment_schedule: PaymentSchedule;
    payment_schedule_day: number;
    payment_schedule_payin_amount: number | null;
    payment_schedule_max_wallet_amount: number | null;
    payment_schedule_max_amount: number | null;
    payment_disabled_date: Date;
    source_of_mandate?: SourceOfMandate;
    direct_debit_status: 'Inactive' | 'Pending' | 'Active';
    iban_last_4: string;
  };
}

export interface PayinRequest {
  params: { resident_id: string };
  body: {
    amount: number;
    note: string;
  };
}

export interface GetAllPaymentInfoResponse {
  data: PaymentInfo[];
}

export interface ResetFailedPendingChargesResponse {
  data: {
    reset_count: number;
  };
}

export interface RevertCashDepositSplit {
  bank_account_amount?: number;
  coins?: number[];
}

export interface RetryCashDepositResponse {
  data: {
    id: string;
    status: string;
  };
}

export interface RevertCashDepositResponse {
  data: {
    id: string | null;
    status: string;
  };
}

export interface IPaymentPhoenixApi extends Api {
  getReceiptBatchPaymentPhoenix(dataIn: {
    params: {
      receiptBatchId: string;
    };
  }): Promise<GetReceiptBatchPaymentPhoenixResult>;

  getAllPaymentInfo(dataIn: {
    params: { nursingHomeId: string };
  }): Promise<GetAllPaymentInfoResponse>;

  getPaymentInfo(dataIn: {
    params: { residentId: string };
  }): Promise<PaymentInfo>;

  getPaymentInfoForFamilyMember(dataIn: {
    params: { tenant: string; residentId: string };
  }): Promise<PaymentInfo>;

  createPayin(dataIn: PayinRequest): Promise<void>;

  resetFailedPendingCharges(dataIn: {
    params: { residentId: string };
    body: { notes: string; pendingChargeId?: string };
  }): Promise<ResetFailedPendingChargesResponse>;

  setPaymentInitiation(dataIn: {
    params: { residentId: string; familyMemberId: string };
    body: PaymentInitiationDto;
  }): Promise<{ success: boolean }>;

  retryCashDeposit(dataIn: {
    params: { residentId: string };
    body: { pending_charge_id: string };
  }): Promise<RetryCashDepositResponse>;

  revertCashDeposit(dataIn: {
    params: { residentId: string };
    body: { pending_charge_id: string; split?: RevertCashDepositSplit };
  }): Promise<RevertCashDepositResponse>;
}

export class PaymentPhoenixApi implements IPaymentPhoenixApi {
  baseUrl: string;

  getReceiptBatchPaymentPhoenix: IPaymentPhoenixApi['getReceiptBatchPaymentPhoenix'];
  getPaymentInfo: IPaymentPhoenixApi['getPaymentInfo'];
  getAllPaymentInfo: IPaymentPhoenixApi['getAllPaymentInfo'];
  getPaymentInfoForFamilyMember: IPaymentPhoenixApi['getPaymentInfoForFamilyMember'];
  createPayin: IPaymentPhoenixApi['createPayin'];
  resetFailedPendingCharges: IPaymentPhoenixApi['resetFailedPendingCharges'];
  setPaymentInitiation: IPaymentPhoenixApi['setPaymentInitiation'];
  retryCashDeposit: IPaymentPhoenixApi['retryCashDeposit'];
  revertCashDeposit: IPaymentPhoenixApi['revertCashDeposit'];

  constructor(
    tenantId: Tenant,
    public authContext: AuthenticationContext,
    apiUrl?: string
  ) {
    this.baseUrl = getPhoenixApiTenantUrl(tenantId, apiUrl);
    const familyMemberBaseUrl = getPhoenixApiTenantUrl('family-member', apiUrl);

    this.getReceiptBatchPaymentPhoenix = get({
      authContext,
      url: (params) =>
        `${this.baseUrl}/receipt-batch/${params.receiptBatchId}/payment`,
    });

    this.getPaymentInfo = get({
      authContext,
      url: (params) =>
        `${this.baseUrl}/resident/${params.residentId}/payment-info`,
    });

    this.getAllPaymentInfo = get({
      authContext,
      url: (params) =>
        `${this.baseUrl}/resident/payment-info?nursing_home_id=${params.nursingHomeId}`,
    });

    this.getPaymentInfoForFamilyMember = get({
      authContext,
      url: (params) =>
        `${familyMemberBaseUrl}/${params.tenant}/${params.residentId}/payment-info`,
    });

    this.createPayin = modify({
      authContext,
      url: (params) => {
        return `${this.baseUrl}/resident/${params.resident_id}/payin`;
      },
    });

    this.resetFailedPendingCharges = modify({
      authContext,
      url: (params) => {
        return `${this.baseUrl}/resident/${params.residentId}/failed-pending-charges`;
      },
      method: 'DELETE',
      transformBody: (body: { notes: string; pendingChargeId?: string }) => ({
        notes: body.notes,
        pending_charge_id: body.pendingChargeId,
      }),
    });

    this.setPaymentInitiation = modify({
      authContext,
      url: (params) => {
        return `${this.baseUrl}/resident/${params.residentId}/family-member/${params.familyMemberId}/payment-initiation`;
      },
      method: 'PUT',
    });

    this.retryCashDeposit = modify({
      authContext,
      url: (params) => {
        return `${this.baseUrl}/resident/${params.residentId}/cash-deposit/retry`;
      },
      method: 'POST',
    });

    this.revertCashDeposit = modify({
      authContext,
      url: (params) => {
        return `${this.baseUrl}/resident/${params.residentId}/cash-deposit/revert`;
      },
      method: 'POST',
    });
  }
}
